import React, { useState } from "react";
import { useVersionPoll } from "../hooks/useVersionPoll";
import { reloadAndWipe, markUpdateLanded } from "../lib/cacheVersion";

// =============================================================================
// UPDATE BANNER
// =============================================================================
// Floats above the app when useVersionPoll detects a new deploy.
// Dismiss is soft — banner re-appears on the next poll tick (~5min) if the
// version is still mismatched, so the rep stays informed until they refresh.
//
// Positioned fixed at top-center, z-40 — below the mobile Sidebar drawer
// (Sidebar.tsx uses z-50 when slid open on mobile) so the nav drawer wins
// when both overlap. Modal portals at z-[100]+ still overlay this banner.
//
// Surface: liquid-glass-light (16px radius, lighter backdrop treatment).
// No transform on hover — per css-glass.md, transform triggers backdrop-filter
// repaint and causes text blur.
// =============================================================================

export default function UpdateBanner(): React.ReactElement | null {
  const { updateAvailable } = useVersionPoll();
  const [dismissed, setDismissed] = useState(false);

  if (!updateAvailable || dismissed) return null;

  // Confirm before reloading — reps can be mid-call filling
  // BookedHandoffModal / Typeform answers. A misclick would wipe unsaved data.
  // Native confirm is deliberate (no custom modal) — it's blocking, it's the
  // platform convention for "are you sure you want to lose state", and there's
  // no chance of it being visually hidden or dismissed by another CSS layer.
  const handleRefresh = () => {
    const ok = window.confirm(
      "Refreshing now will reload the page and clear any unsaved form data. Continue?"
    );
    if (ok) {
      // Same breadcrumb as the silent clean-tab path so the "update just landed"
      // toast greets them on the fresh page — consistent feedback either way.
      markUpdateLanded();
      reloadAndWipe();
    }
  };

  return (
    <div
      className="fixed top-4 left-1/2 -translate-x-1/2 z-40 w-max max-w-[calc(100vw-2rem)]"
      role="status"
      aria-live="polite"
    >
      {/* liquid-glass-light surface — lighter than liquid-glass cards, correct
          for banners/pills. overflow:hidden is set by the class itself. */}
      <div className="liquid-glass-light bg-ds-accent/10 flex flex-col sm:flex-row items-center gap-3 px-5 py-3">
        {/* Text block */}
        <div className="flex flex-col items-center sm:items-start gap-0.5 text-center sm:text-left">
          <span className="token-14 font-medium text-glass">New version available</span>
          <span className="token-12 text-glass opacity-70">
            Refresh now to get the latest features and fixes.
          </span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0">
          {/* Primary CTA — border glow only on hover, no transform */}
          <button
            onClick={handleRefresh}
            className="token-12 font-medium px-4 py-1.5 text-ds-accent bg-ds-accent/15 border border-ds-accent/30 hover:bg-ds-accent/25 hover:border-ds-accent/60 anim-tap transition-colors duration-150"
            style={{ borderRadius: 8 }}
          >
            Refresh now
          </button>

          {/* Dismiss — soft, no color association */}
          <button
            onClick={() => setDismissed(true)}
            aria-label="Dismiss update banner"
            className="token-14 text-glass opacity-50 hover:opacity-90 anim-tap transition-opacity duration-150 leading-none px-1"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
}
